<template>
    <p v-if="state === 'quiet'" class="address-status address-status--quiet" />

    <p v-else :class="['address-status', `address-status--${state}`]">
        <i :class="['pi', ICONS[state]]" aria-hidden="true" />
        <span>{{ text }}</span>
    </p>
</template>

<script setup>
import { computed } from 'vue'

// What is known about the address on screen, in one row.
//
// One row and one message: the form used to stack a not-configured note, a
// failure note, a refusal and a bordered correction box, and three of them could
// be on screen together. Only one of them was ever the thing to read.
//
// It holds nothing and decides nothing.
const props = defineProps({
    // Whether an address can be looked up at all. False draws the manual form,
    // and this row says why it is there.
    available: { type: Boolean, default: true },
    // What the address itself records: true when it came from the address
    // service, false when it was typed by hand, and null when nobody said.
    verified: { type: Boolean, default: null },
    // Whether there is an address at all yet.
    present: { type: Boolean, default: false }
})

const ICONS = Object.freeze({
    unavailable: 'pi-info-circle',
    unverified: 'pi-exclamation-circle',
    verified: 'pi-check-circle'
})

/**
 * Which single thing to say, first match winning.
 *
 * Being unable to look an address up outranks what the address says about
 * itself, because it is the reason the form looks the way it does. After that
 * the address speaks for itself, and an address nobody has entered says nothing.
 */
const state = computed(function () {
    if (props.available === false) {
        return 'unavailable'
    }
    if (props.present === false || props.verified === null) {
        return 'quiet'
    }
    return props.verified ? 'verified' : 'unverified'
})

const text = computed(function () {
    if (state.value === 'unavailable') {
        return 'Addresses cannot be looked up right now. What is typed here is saved as unverified.'
    }
    if (state.value === 'unverified') {
        return 'Not verified — this address was typed by hand. Search for it to verify it.'
    }
    return 'Verified'
})
</script>

<style scoped>
.address-status {
    align-items: baseline;
    display: flex;
    font-size: 12px;
    gap: 6px;
    line-height: 1.45;
    margin: 10px 0 0;
}

.address-status--quiet {
    display: none;
}

.address-status--verified {
    color: var(--p-primary-color);
}

.address-status--unverified,
.address-status--unavailable {
    color: var(--p-surface-400);
}

.address-status .pi {
    font-size: 12px;
}
</style>
